import { Avatar, Box, Button, Flex, Grid, GridItem, Heading, HStack, Icon, IconButton, keyframes, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  PiListChecksDuotone, PiUsersThreeDuotone, PiPlayCircleDuotone, PiChartPieSliceDuotone, PiLinkDuotone,
  PiCameraDuotone, PiSealCheckDuotone, PiGearDuotone, PiBriefcaseDuotone, PiUserCircleDuotone,
  PiClockCounterClockwiseDuotone, PiCaretRightBold, PiBroadcastDuotone, PiClockDuotone, PiNotePencilDuotone,
  PiCheckCircleDuotone, PiUserPlusDuotone, PiTimerDuotone, PiTrophyDuotone, PiUsersDuotone, PiChartBarDuotone,
  PiTrendUpDuotone, PiCompassDuotone, PiLightningDuotone, PiPlusBold, PiArrowRightBold, PiRocketLaunchDuotone,
  PiShieldCheckDuotone, PiCubeDuotone, PiCalendarBlankDuotone, PiFlagCheckeredDuotone, PiFlagPennantDuotone, PiPulseDuotone
} from "react-icons/pi";
import { withModel, VOTING_STATUSES } from "@lubbezposrednio-frontend/core";
import { OrganizerDashboardModel, ActionItem, ActionKind, ActivityEvent, ActivityKind } from "./OrganizerDashboardModel";
import { DashboardSkeleton, RailSkeleton } from "../../components/skeletons";

interface Props {
  model: OrganizerDashboardModel;
}

// ── helpers ──
const drgba = (hex: string, a: number) => {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const plural = (n: number, one: string, few: string, many: string) =>
  n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? few : many;

const compactNum = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n}`);

const timeAgo = (ts: number) => {
  const diff = Date.now() - ts;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000);
  if (d >= 1) return `${d} ${plural(d, "dzień", "dni", "dni")} temu`;
  if (h >= 1) return `${h} ${plural(h, "godzinę", "godziny", "godzin")} temu`;
  return "przed chwilą";
};

const countdown = (end: number) => {
  const diff = end - Date.now();
  if (diff <= 0) return "kończy się";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const min = Math.floor((diff % 3600000) / 60000);
  if (d >= 1) return `${d} ${plural(d, "dzień", "dni", "dni")} ${h} godz.`;
  if (h >= 1) return `${h} godz. ${min} min`;
  return `${min} min`;
};

const signupCloses = (iso: string | null) => {
  if (!iso) return "Zapisy otwarte";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return "Zapisy zamknięte";
  const d = Math.floor(diff / 86400000);
  if (d >= 1) return `Zamknięcie za ${d} ${plural(d, "dzień", "dni", "dni")}`;
  const h = Math.floor(diff / 3600000);
  return `Zamknięcie za ${h} godz.`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 6) return "Dobrej nocy";
  if (h < 12) return "Dzień dobry";
  if (h < 18) return "Cześć";
  return "Dobry wieczór";
};

const todayLabel = () => new Date().toLocaleDateString("pl-PL", { weekday: "long", day: "numeric", month: "long" });

// ── meta ──
const STATUS_META: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  [VOTING_STATUSES.ONGOING]: { label: "Trwa", color: "#1f9e18", icon: PiBroadcastDuotone },
  [VOTING_STATUSES.PLANNED]: { label: "Wkrótce", color: "#7b2ff7", icon: PiClockDuotone },
  [VOTING_STATUSES.DRAFT]: { label: "Szkic", color: "#6b7280", icon: PiNotePencilDuotone },
  [VOTING_STATUSES.FINISHED]: { label: "Zakończone", color: "#2563eb", icon: PiCheckCircleDuotone }
};

const ACTION_META: Record<ActionKind, { label: string; cta: string; icon: React.ElementType; color: string }> = {
  vote: { label: "Możesz zagłosować", cta: "Zagłosuj", icon: PiCheckCircleDuotone, color: "#1f9e18" },
  results: { label: "Wyniki gotowe", cta: "Zobacz", icon: PiChartPieSliceDuotone, color: "#2563eb" },
  draft: { label: "Dokończ szkic", cta: "Dokończ", icon: PiNotePencilDuotone, color: "#6b7280" },
  signup: { label: "Otwarte zapisy", cta: "Zarządzaj", icon: PiUserPlusDuotone, color: "#430098" },
  confirm_group: { label: "Potwierdź członkostwo", cta: "Przejdź", icon: PiUsersThreeDuotone, color: "#b45309" }
};

const ACTIVITY_META: Record<ActivityKind, { icon: React.ElementType; color: string; text: (t: string) => string }> = {
  started: { icon: PiPlayCircleDuotone, color: "#1f9e18", text: t => `Głosowanie „${t}” ruszyło` },
  results: { icon: PiChartPieSliceDuotone, color: "#2563eb", text: t => `Wyniki „${t}” są gotowe` },
  anchored: { icon: PiLinkDuotone, color: "#430098", text: t => `„${t}” zakotwiczono na blockchainie` }
};

// ── design tokens ──
const GLASS = {
  background: "rgba(255,255,255,0.58)",
  backdropFilter: "blur(24px) saturate(155%)",
  WebkitBackdropFilter: "blur(24px) saturate(155%)",
  border: "1px solid rgba(255,255,255,0.6)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 18px 44px -26px rgba(43,0,90,0.42)"
} as const;

const SCROLL_FOG = {
  overflowY: "auto",
  scrollbarWidth: "none",
  "::-webkit-scrollbar": { display: "none" },
  maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 22px), transparent 100%)",
  WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 22px), transparent 100%)"
} as const;

const fadeUp = keyframes`from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); }`;

const pulse = keyframes`0% { box-shadow: 0 0 0 0 rgba(31,158,24,0.5); } 70% { box-shadow: 0 0 0 6px rgba(31,158,24,0); } 100% { box-shadow: 0 0 0 0 rgba(31,158,24,0); }`;

const EASE = "0.16s cubic-bezier(0.22, 1, 0.36, 1)";

// ── prymitywy bento ──
const Tile: React.FC<{
  i?: number;
  colSpan?: any;
  rowSpan?: any;
  glow?: string;
  interactive?: boolean;
  to?: string;
  children: React.ReactNode;
  sx?: any;
}> = ({ i = 0, colSpan = 1, rowSpan = 1, glow, interactive, to, children, sx }) => (
  <GridItem
    {...(to ? { as: Link, to } : {})}
    colSpan={colSpan}
    rowSpan={rowSpan}
    position="relative"
    overflow="hidden"
    borderRadius="22px"
    p="18px"
    display="flex"
    flexDirection="column"
    minH={0}
    sx={{ ...GLASS, ...sx }}
    animation={`${fadeUp} 0.5s cubic-bezier(0.22,1,0.36,1) both`}
    style={{ animationDelay: `${Math.min(i, 12) * 0.04}s` }}
    transition={`transform ${EASE}, box-shadow ${EASE}`}
    _hover={interactive || to ? { transform: "translateY(-3px)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 22px 50px -22px rgba(43,0,90,0.5)", textDecoration: "none" } : undefined}
  >
    {glow && (
      <Box position="absolute" top="-70px" right="-50px" w="200px" h="200px" borderRadius="full" pointerEvents="none" sx={{ background: `radial-gradient(circle, ${drgba(glow, 0.16)}, transparent 70%)` }} />
    )}
    <Box position="relative" display="flex" flexDirection="column" flex="1" minH={0}>
      {children}
    </Box>
  </GridItem>
);

const IconChip: React.FC<{ icon: React.ElementType; color: string; size?: string; box?: string }> = ({ icon, color, size = "22px", box = "40px" }) => (
  <Flex w={box} h={box} borderRadius="13px" align="center" justify="center" bg={drgba(color, 0.14)} flexShrink={0}>
    <Icon as={icon} boxSize={size} color={color} />
  </Flex>
);

// Kompaktowy nagłówek wewnątrz kafla
const TileHead: React.FC<{ icon: React.ElementType; title: string; color: string; count?: number; to?: string }> = ({ icon, title, color, count, to }) => (
  <Flex justify="space-between" align="center" mb="12px" flexShrink={0}>
    <HStack spacing="9px" minW={0}>
      <IconChip icon={icon} color={color} box="34px" size="19px" />
      <Text fontSize="14.5px" fontWeight="800" color="#241536" noOfLines={1} letterSpacing="-0.01em">{title}</Text>
      {count !== undefined && count > 0 && (
        <Flex minW="20px" h="20px" px="6px" bg={color} color="white" borderRadius="full" align="center" justify="center" fontSize="11px" fontWeight="800" flexShrink={0}>
          {count}
        </Flex>
      )}
    </HStack>
    {to && <Icon as={PiCaretRightBold} boxSize="14px" color="#c9c3d6" flexShrink={0} />}
  </Flex>
);

// KPI kafel 1×1
const KpiTile: React.FC<{ i: number; icon: React.ElementType; value: string; label: string; color: string; sub?: string; to?: string }> = ({ i, icon, value, label, color, sub, to }) => (
  <Tile i={i} glow={color} to={to} interactive>
    <Flex direction="column" justify="space-between" h="100%">
      <IconChip icon={icon} color={color} />
      <Box>
        <Text fontSize="32px" fontWeight="900" color="#1b1030" lineHeight="1" letterSpacing="-0.03em">{value}</Text>
        <Text fontSize="13px" color="#6b6480" fontWeight="700" mt="4px" noOfLines={1}>{label}</Text>
        {sub && <Text fontSize="11.5px" color={color} fontWeight="800" mt="2px" noOfLines={1}>{sub}</Text>}
      </Box>
    </Flex>
  </Tile>
);

// Kafel powitalny / status (zamiast osobnego hero) — gradient
const StatusTile: React.FC<{ i: number; model: OrganizerDashboardModel; statusLine: string }> = ({ i, model, statusLine }) => (
  <GridItem
    colSpan={2}
    rowSpan={2}
    position="relative"
    overflow="hidden"
    borderRadius="22px"
    bgGradient="linear(150deg, #340074 0%, #5a13c4 55%, #7b2ff7 100%)"
    boxShadow="0 22px 52px -20px rgba(67,0,152,0.55)"
    animation={`${fadeUp} 0.5s cubic-bezier(0.22,1,0.36,1) both`}
  >
    <Box position="absolute" top="-90px" right="-60px" w="260px" h="260px" borderRadius="full" bg="rgba(255,255,255,0.09)" />
    <Box position="absolute" bottom="-120px" right="90px" w="230px" h="230px" borderRadius="full" bg="rgba(229,31,68,0.22)" filter="blur(6px)" />
    <Flex position="relative" direction="column" h="100%" p={{ base: "22px", md: "26px" }}>
      <Flex justify="space-between" align="flex-start">
        <Text color="rgba(255,255,255,0.72)" fontWeight="800" fontSize="12px" textTransform="uppercase" letterSpacing="0.09em">
          {todayLabel()}
        </Text>
        <Avatar size="md" name={model.userName || "?"} src={model.avatarUrl || undefined} bg="rgba(255,255,255,0.2)" color="white" borderWidth="2px" borderColor="rgba(255,255,255,0.4)" />
      </Flex>
      <Box mt="14px">
        <Heading color="white" fontSize={{ base: "26px", md: "31px" }} fontWeight="900" letterSpacing="-0.02em" lineHeight="1.08" noOfLines={2}>
          {greeting()}{model.userName ? `, ${model.userName}` : ""}! 👋
        </Heading>
        <Text color="rgba(255,255,255,0.9)" fontSize={{ base: "14.5px", md: "16px" }} mt="9px" noOfLines={3}>
          {statusLine}
        </Text>
      </Box>
      <HStack spacing="10px" mt="auto" pt="18px" flexWrap="wrap">
        <Button as={Link} to="/organizator/utworz-glosowanie" leftIcon={<Icon as={PiPlusBold} />} bg="white" color="primary" borderRadius="13px" fontWeight="800" size="sm" px="16px" boxShadow="0 8px 20px rgba(0,0,0,0.2)" _hover={{ bg: "rgba(255,255,255,0.92)", transform: "translateY(-1px)" }}>
          Utwórz głosowanie
        </Button>
        <Button as={Link} to="/organizator/odkrywaj" leftIcon={<Icon as={PiCompassDuotone} />} variant="outline" size="sm" borderColor="rgba(255,255,255,0.5)" color="white" borderRadius="13px" fontWeight="700" _hover={{ bg: "rgba(255,255,255,0.14)" }}>
          Społeczność
        </Button>
      </HStack>
    </Flex>
  </GridItem>
);

// Wiersz „wymaga uwagi”
const AttnRow: React.FC<{ item: ActionItem }> = ({ item }) => {
  const m = ACTION_META[item.kind];
  return (
    <HStack as={Link} to={item.to} spacing="11px" py="8px" px="8px" borderRadius="12px" transition={`background ${EASE}`} _hover={{ bg: drgba(m.color, 0.08), textDecoration: "none" }}>
      <IconChip icon={m.icon} color={m.color} box="36px" size="19px" />
      <Box flex="1" minW={0}>
        <Text fontSize="10.5px" fontWeight="800" color={m.color} textTransform="uppercase" letterSpacing="0.05em">{m.label}</Text>
        <Text fontWeight="700" color="#2f2a3d" fontSize="13.5px" noOfLines={1}>{item.title}</Text>
      </Box>
      <Icon as={PiCaretRightBold} boxSize="13px" color="#c9c3d6" flexShrink={0} />
    </HStack>
  );
};

// Wiersz głosowania (wewnątrz kafla)
const VotingRow: React.FC<{ id: string; title?: string; status?: string; to: string; voters?: number; votes?: number }> = ({ title, status, to, voters, votes }) => {
  const m = status ? STATUS_META[status] : undefined;
  const turnout = voters && votes !== undefined && voters > 0 ? Math.round((votes / voters) * 100) : undefined;
  return (
    <HStack as={Link} to={to} spacing="11px" py="8px" px="8px" borderRadius="12px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
      {m && <IconChip icon={m.icon} color={m.color} box="36px" size="18px" />}
      <Box flex="1" minW={0}>
        <Text fontWeight="700" color="#2f2a3d" fontSize="13.5px" noOfLines={1}>{title || "Szkic bez tytułu"}</Text>
        <HStack spacing="9px" mt="1px" color="#a8a2b6" fontSize="11.5px" fontWeight="600">
          {m && <Text color={m.color} fontWeight="800">{m.label}</Text>}
          {voters !== undefined && voters > 0 && (
            <HStack spacing="3px"><Icon as={PiUsersDuotone} boxSize="12px" /><Text>{compactNum(voters)}</Text></HStack>
          )}
          {turnout !== undefined && (
            <HStack spacing="3px"><Icon as={PiTrendUpDuotone} boxSize="12px" /><Text>{turnout}%</Text></HStack>
          )}
        </HStack>
      </Box>
      <Icon as={PiCaretRightBold} boxSize="13px" color="#c9c3d6" flexShrink={0} />
    </HStack>
  );
};

// Karta mini-głosowania (odkrywaj / udział)
const MiniVotingCard: React.FC<{ id: string; title?: string; status?: string }> = ({ id, title, status }) => {
  const m = status ? STATUS_META[status] : undefined;
  return (
    <VStack as={Link} to={`/glosowanie/${id}`} align="stretch" spacing="9px" p="15px" h="100%" borderRadius="16px" sx={GLASS} transition={`transform ${EASE}, box-shadow ${EASE}`} _hover={{ transform: "translateY(-3px)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 20px 44px -20px rgba(43,0,90,0.5)", textDecoration: "none" }}>
      {m && (
        <HStack spacing="6px" w="fit-content" bg={drgba(m.color, 0.12)} px="9px" py="3px" borderRadius="full">
          <Icon as={m.icon} boxSize="13px" color={m.color} />
          <Text fontSize="11px" fontWeight="800" color={m.color}>{m.label}</Text>
        </HStack>
      )}
      <Text fontWeight="700" color="#2f2a3d" fontSize="14px" noOfLines={2} flex="1">{title || "Głosowanie"}</Text>
      <HStack spacing="5px" color="accent" fontWeight="800" fontSize="12px">
        <Text>Otwórz</Text>
        <Icon as={PiArrowRightBold} boxSize="12px" />
      </HStack>
    </VStack>
  );
};

// Pierścień postępu (rekord frekwencji)
const Ring: React.FC<{ pct: number; color: string }> = ({ pct, color }) => {
  const r = 26;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.min(pct, 100) / 100);
  return (
    <Box position="relative" w="64px" h="64px" flexShrink={0}>
      <svg width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke={drgba(color, 0.16)} strokeWidth="7" />
        <circle cx="32" cy="32" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 32 32)" />
      </svg>
      <Flex position="absolute" inset={0} align="center" justify="center">
        <Text fontSize="14px" fontWeight="900" color="#1b1030">{pct}%</Text>
      </Flex>
    </Box>
  );
};

// Donut statusów głosowań
const DonutStatus: React.FC<{ segments: { label: string; value: number; color: string }[]; total: number }> = ({ segments, total }) => {
  const r = 46;
  const c = 2 * Math.PI * r;
  const sw = 15;
  let acc = 0;
  return (
    <Flex flex="1" align="center" gap="16px" minH={0}>
      <Box position="relative" w="128px" h="128px" flexShrink={0}>
        <svg width="128" height="128" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(24,12,48,0.06)" strokeWidth={sw} />
          {total > 0 &&
            segments.filter(s => s.value > 0).map(s => {
              const len = (c * s.value) / total;
              const el = (
                <circle key={s.label} cx="64" cy="64" r={r} fill="none" stroke={s.color} strokeWidth={sw} strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-acc} transform="rotate(-90 64 64)" />
              );
              acc += len;
              return el;
            })}
        </svg>
        <Flex position="absolute" inset={0} direction="column" align="center" justify="center">
          <Text fontSize="26px" fontWeight="900" color="#1b1030" lineHeight="1">{total}</Text>
          <Text fontSize="10.5px" color="#a8a2b6" fontWeight="700">głosowań</Text>
        </Flex>
      </Box>
      <VStack align="stretch" spacing="7px" flex="1" minW={0}>
        {segments.map(s => (
          <HStack key={s.label} spacing="8px">
            <Box w="9px" h="9px" borderRadius="3px" bg={s.color} flexShrink={0} />
            <Text fontSize="12.5px" color="#6b6480" fontWeight="600" flex="1" noOfLines={1}>{s.label}</Text>
            <Text fontSize="12.5px" color="#241536" fontWeight="800">{s.value}</Text>
          </HStack>
        ))}
      </VStack>
    </Flex>
  );
};

const whenLabel = (ts: number) => {
  const diff = ts - Date.now();
  if (diff <= 0) return "teraz";
  const d = Math.floor(diff / 86400000);
  if (d >= 1) return `za ${d} ${plural(d, "dzień", "dni", "dni")}`;
  const h = Math.floor(diff / 3600000);
  if (h >= 1) return `za ${h} godz.`;
  return `za ${Math.floor(diff / 60000)} min`;
};

// Wiersz „na żywo” — trwające głosowanie z paskiem oddanych głosów + odliczaniem
const LiveRow: React.FC<{ id: string; title: string; end?: string; voters?: number; votes?: number }> = ({ id, title, end, voters, votes }) => {
  const pct = voters && voters > 0 && votes !== undefined ? Math.min(100, Math.round((votes / voters) * 100)) : 0;
  return (
    <Box as={Link} to={`/organizator/glosowanie/${id}`} py="8px" px="8px" borderRadius="12px" transition={`background ${EASE}`} _hover={{ bg: "rgba(31,158,24,0.06)", textDecoration: "none" }} display="block">
      <Flex justify="space-between" align="center" mb="6px" gap="10px">
        <HStack spacing="7px" minW={0}>
          <Box w="7px" h="7px" borderRadius="full" bg="#1f9e18" flexShrink={0} sx={{ boxShadow: "0 0 0 0 rgba(31,158,24,0.5)", animation: `${pulse} 1.8s ease-in-out infinite` }} />
          <Text fontWeight="700" color="#2f2a3d" fontSize="13.5px" noOfLines={1}>{title}</Text>
        </HStack>
        {end && <Text fontSize="11px" color="#a8a2b6" fontWeight="700" flexShrink={0}>{countdown(new Date(end).getTime())}</Text>}
      </Flex>
      <Flex align="center" gap="9px">
        <Box flex="1" h="7px" borderRadius="full" bg="rgba(31,158,24,0.14)" overflow="hidden">
          <Box h="100%" w={`${pct}%`} bgGradient="linear(90deg, #1f9e18, #46c93a)" borderRadius="full" transition="width 0.6s cubic-bezier(0.22,1,0.36,1)" />
        </Box>
        <Text fontSize="11.5px" fontWeight="800" color="#1f9e18" w="34px" textAlign="right">{pct}%</Text>
      </Flex>
    </Box>
  );
};

// Wiersz słupka frekwencji
const BarRow: React.FC<{ title: string; pct: number; color: string }> = ({ title, pct, color }) => (
  <Box py="6px">
    <Flex justify="space-between" align="center" mb="4px" gap="10px">
      <Text fontSize="12.5px" color="#4a4458" fontWeight="600" noOfLines={1}>{title}</Text>
      <Text fontSize="12px" fontWeight="800" color={color} flexShrink={0}>{pct}%</Text>
    </Flex>
    <Box h="9px" borderRadius="full" bg={drgba(color, 0.12)} overflow="hidden">
      <Box h="100%" w={`${Math.min(100, pct)}%`} borderRadius="full" bgGradient={`linear(90deg, ${color}, ${drgba(color, 0.75)})`} transition="width 0.7s cubic-bezier(0.22,1,0.36,1)" />
    </Box>
  </Box>
);

// Wiersz osi czasu
const TimelineRow: React.FC<{ kind: "start" | "end"; id: string; title: string; at: number; last: boolean }> = ({ kind, id, title, at, last }) => {
  const color = kind === "start" ? "#7b2ff7" : "#e51f44";
  const ico = kind === "start" ? PiFlagPennantDuotone : PiFlagCheckeredDuotone;
  return (
    <HStack as={Link} to={`/organizator/glosowanie/${id}`} align="stretch" spacing="11px" _hover={{ textDecoration: "none" }} role="group">
      <VStack spacing={0} flexShrink={0}>
        <Flex w="30px" h="30px" borderRadius="10px" bg={drgba(color, 0.14)} align="center" justify="center">
          <Icon as={ico} boxSize="16px" color={color} />
        </Flex>
        {!last && <Box flex="1" w="2px" bg="rgba(24,12,48,0.08)" my="2px" borderRadius="full" />}
      </VStack>
      <Box flex="1" minW={0} pb={last ? "0" : "12px"} pt="3px">
        <Text fontSize="10.5px" fontWeight="800" color={color} textTransform="uppercase" letterSpacing="0.05em">
          {kind === "start" ? "Start" : "Koniec"} · {whenLabel(at)}
        </Text>
        <Text fontWeight="700" color="#2f2a3d" fontSize="13.5px" noOfLines={1} _groupHover={{ color: "primary" }}>{title}</Text>
      </Box>
    </HStack>
  );
};

// Sekcja profilu (góra prawego panelu)
const AccountCard: React.FC<{ userName: string; avatarUrl?: string; verified: boolean; profileRef?: string; isOrgAccount: boolean }> = ({ userName, avatarUrl, verified, profileRef, isOrgAccount }) => (
  <Box position="relative">
    <IconButton
      as={Link}
      to="/organizator/konto"
      aria-label="Ustawienia konta"
      icon={<Icon as={PiGearDuotone} boxSize="18px" />}
      position="absolute"
      top="-2px"
      right="-2px"
      size="sm"
      variant="ghost"
      color="#9a94ab"
      borderRadius="10px"
      _hover={{ bg: "rgba(24,12,48,0.06)", color: "primary" }}
    />

    <VStack spacing="10px" pt="2px">
      <Box position="relative" role="group">
        <Avatar
          size="xl"
          name={userName || "?"}
          src={avatarUrl || undefined}
          bg="primary"
          color="white"
          fontWeight="800"
        />
        <Flex as={Link} to="/organizator/konto" position="absolute" inset={0} direction="column" align="center" justify="center" gap="1px" borderRadius="full" bg="rgba(20,10,40,0.5)" color="white" opacity={0} transition="opacity 0.16s ease" _groupHover={{ opacity: 1 }}>
          <Icon as={PiCameraDuotone} boxSize="19px" />
          <Text fontSize="10px" fontWeight="700">Zmień</Text>
        </Flex>
        {verified && (
          <Flex position="absolute" bottom="1px" right="1px" w="24px" h="24px" bg="#1d9bf0" borderRadius="full" align="center" justify="center" border="3px solid white" pointerEvents="none">
            <Icon as={PiSealCheckDuotone} boxSize="13px" color="white" />
          </Flex>
        )}
      </Box>

      <VStack spacing="7px">
        <Text fontWeight="900" fontSize="19px" color="#1b1030" noOfLines={1} textAlign="center" letterSpacing="-0.01em">{userName || "Twoje konto"}</Text>
        <HStack spacing="5px" px="11px" py="3px" borderRadius="full" bg="rgba(67,0,152,0.08)">
          <Icon as={isOrgAccount ? PiBriefcaseDuotone : PiUserCircleDuotone} boxSize="13px" color="primary" />
          <Text fontSize="11px" fontWeight="800" color="primary" textTransform="uppercase" letterSpacing="0.05em">{isOrgAccount ? "Organizacja" : "Osoba"}</Text>
        </HStack>
        {profileRef && (
          <Text as={Link} to={`/profil/${profileRef}`} fontSize="12px" fontWeight="600" color="#9a94ab" fontFamily="mono" _hover={{ color: "accent" }}>
            @{profileRef.replace(/^@+/, "")}
          </Text>
        )}
      </VStack>
    </VStack>
  </Box>
);

// Pro-nagłówek prawego panelu
const PanelHeading: React.FC<{ icon: React.ElementType; title: string; color?: string; to?: string }> = ({ icon, title, color = "#430098", to }) => (
  <Flex justify="space-between" align="center" mb="11px">
    <HStack spacing="8px">
      <Icon as={icon} boxSize="18px" color={color} />
      <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.08em">{title}</Text>
    </HStack>
    {to && (
      <Text as={Link} to={to} fontSize="12px" fontWeight="700" color={color} _hover={{ textDecoration: "underline" }}>
        Wszystkie
      </Text>
    )}
  </Flex>
);

const FeedItem: React.FC<{ event: ActivityEvent }> = ({ event }) => {
  const m = ACTIVITY_META[event.kind];
  return (
    <HStack as={Link} to={`/glosowanie/${event.id}`} align="flex-start" spacing="10px" py="6px" px="6px" borderRadius="10px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
      <Flex w="28px" h="28px" borderRadius="9px" bg={drgba(m.color, 0.14)} align="center" justify="center" flexShrink={0} mt="1px">
        <Icon as={m.icon} boxSize="16px" color={m.color} />
      </Flex>
      <Box flex="1" minW={0}>
        <Text fontSize="13px" color="#3f3a4d" noOfLines={2} lineHeight="1.3" fontWeight="500">{m.text(event.title)}</Text>
        <Text fontSize="11px" color="#a8a2b6" fontWeight="600" mt="1px">{timeAgo(event.at)}</Text>
      </Box>
    </HStack>
  );
};

// Piękny kafel-akcja (prawy panel)
const ActionTile: React.FC<{ icon: React.ElementType; label: string; to: string; color: string }> = ({ icon, label, to, color }) => (
  <VStack
    as={Link}
    to={to}
    spacing="9px"
    py="16px"
    px="10px"
    borderRadius="16px"
    border="1px solid"
    borderColor={drgba(color, 0.18)}
    bg={drgba(color, 0.06)}
    transition={`all ${EASE}`}
    _hover={{ bg: drgba(color, 0.12), transform: "translateY(-2px)", textDecoration: "none", boxShadow: `0 12px 26px -12px ${drgba(color, 0.6)}` }}
    _active={{ transform: "translateY(0)" }}
  >
    <Flex w="44px" h="44px" borderRadius="13px" align="center" justify="center" bg={drgba(color, 0.14)} color={color}>
      <Icon as={icon} boxSize="24px" />
    </Flex>
    <Text fontSize="13px" fontWeight="800" color="#1b1030" textAlign="center" lineHeight="1.2">{label}</Text>
  </VStack>
);

// Zawartość prawego panelu — współdzielona: glass-aside (≥xl) oraz zwinięta w głównej kolumnie (<xl).
const RightRail: React.FC<{ model: OrganizerDashboardModel }> = observer(({ model }) => (
  <>
    <AccountCard userName={model.userName} avatarUrl={model.avatarUrl} verified={model.verified} profileRef={model.profileRef} isOrgAccount={model.isOrgAccount} />

    <Box borderTop="1px solid rgba(67,0,152,0.08)" my="22px" />

    <SimpleGrid columns={2} spacing="10px">
      <ActionTile icon={PiListChecksDuotone} label="Nowe głosowanie" to="/organizator/utworz-glosowanie" color="#e51f44" />
      <ActionTile icon={PiUsersThreeDuotone} label="Nowa grupa" to="/organizator/grupy/nowa" color="#10b981" />
    </SimpleGrid>

    {model.activityFeed.length > 0 && (
      <>
        <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />
        <PanelHeading icon={PiClockCounterClockwiseDuotone} title="Co się działo" color="#7c3aed" />
        <VStack align="stretch" spacing="1px">
          {model.activityFeed.slice(0, 6).map((e, i) => (
            <FeedItem key={`${e.kind}-${e.id}-${i}`} event={e} />
          ))}
        </VStack>
      </>
    )}

    {model.myGroups.length > 0 && (
      <>
        <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />
        <PanelHeading icon={PiUsersThreeDuotone} title="Twoje grupy" color="#10b981" to="/organizator/grupy" />
        <VStack align="stretch" spacing="2px">
          {model.myGroups.slice(0, 4).map(g => {
            const pending = g.membership_status === "pending";
            return (
              <HStack key={g.id} as={Link} to={`/organizator/grupy/${g.id}`} align="center" spacing="10px" py="6px" px="6px" borderRadius="11px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
                <Avatar
                  size="sm"
                  boxSize="34px"
                  borderRadius="10px"
                  name={g.name}
                  src={g.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${g.avatar_url}` : undefined}
                  bg={drgba("#10b981", 0.14)}
                  color="#10b981"
                  icon={<Icon as={PiUsersThreeDuotone} boxSize="18px" color="#10b981" />}
                  flexShrink={0}
                />
                <Box flex="1" minW={0}>
                  <HStack spacing="6px" align="center">
                    <Text fontWeight="700" color="#2f2a3d" fontSize="13.5px" noOfLines={1}>{g.name}</Text>
                    {pending && (
                      <Box px="6px" py="1px" borderRadius="full" bg={drgba("#f59e0b", 0.16)} flexShrink={0}>
                        <Text fontSize="9.5px" fontWeight="800" color="#b45309" textTransform="uppercase" letterSpacing="0.04em">oczekuje</Text>
                      </Box>
                    )}
                  </HStack>
                  <Text fontSize="11.5px" color="#a8a2b6" fontWeight="600" mt="1px">
                    {g.upcoming_votings.length > 0
                      ? `${g.upcoming_votings.length} ${plural(g.upcoming_votings.length, "nadchodzące", "nadchodzące", "nadchodzących")}`
                      : `${g.members_count} ${plural(g.members_count, "członek", "członków", "członków")}`}
                  </Text>
                </Box>
                <Icon as={PiCaretRightBold} boxSize="12px" color="#c9c3d6" flexShrink={0} />
              </HStack>
            );
          })}
        </VStack>
      </>
    )}
  </>
));

const OrganizerDashboardView: React.FC<Props> = observer(({ model }) => {
  useEffect(() => {
    document.title = "Pulpit";
  }, []);

  const toVote = model.toVoteParticipations.length;
  const ongoing = model.ongoingParticipationsCount;
  const resultsReady = model.resultsReadyParticipations.length;

  const statusParts: string[] = [];
  if (toVote) statusParts.push(`${toVote} ${plural(toVote, "głos do oddania", "głosy do oddania", "głosów do oddania")}`);
  if (ongoing) statusParts.push(`${ongoing} ${plural(ongoing, "trwające", "trwające", "trwających")}`);
  if (resultsReady) statusParts.push(`${resultsReady} ${plural(resultsReady, "wynik gotowy", "wyniki gotowe", "wyników gotowych")}`);
  const statusLine = statusParts.length ? `Masz ${statusParts.join(", ")}.` : "Wszystko ogarnięte — nic nie czeka na Twój ruch.";

  const actions = model.actionItems;
  const isOrg = model.isOrganizer;
  const ongoingVotings = model.countBy(VOTING_STATUSES.ONGOING);
  const turnout = model.totalVoters > 0 ? Math.round((model.totalVotes / model.totalVoters) * 100) : 0;

  const statusSegments = [
    { label: "Trwające", value: model.ongoingCount, color: "#1f9e18" },
    { label: "Zaplanowane", value: model.plannedCount, color: "#7b2ff7" },
    { label: "Zakończone", value: model.finishedCount, color: "#2563eb" },
    { label: "Szkice", value: model.draftCount, color: "#9aa0b0" }
  ];
  const statusTotal = statusSegments.reduce((s, x) => s + x.value, 0);

  // ── derywacje nowych widgetów ──
  const liveVotings = model.votings.filter(v => v.status === VOTING_STATUSES.ONGOING).slice(0, 5);
  const turnoutBars = model.votings
    .filter(v => v.status === VOTING_STATUSES.FINISHED && (v.voters_count ?? 0) > 0)
    .map(v => ({ id: v.id, title: v.title || "Głosowanie", pct: Math.round(((v.votes_count ?? 0) / (v.voters_count ?? 1)) * 100) }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 6);
  const lastAnchored = model.votings
    .filter(v => v.status === VOTING_STATUSES.FINISHED && v.end_date)
    .sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime())[0];
  const timeline = [
    ...model.votings.filter(v => v.status === VOTING_STATUSES.PLANNED && v.start_date).map(v => ({ kind: "start" as const, id: v.id, title: v.title || "Głosowanie", at: new Date(v.start_date).getTime() })),
    ...model.votings.filter(v => v.status === VOTING_STATUSES.ONGOING && v.end_date).map(v => ({ kind: "end" as const, id: v.id, title: v.title || "Głosowanie", at: new Date(v.end_date).getTime() }))
  ]
    .filter(e => e.at > Date.now())
    .sort((a, b) => a.at - b.at)
    .slice(0, 5);

  if (model.isLoading) {
    return (
      <Flex align="flex-start">
        <Box flex="1" minW={0}>
          <Box w="100%" maxW="1680px" mx="auto" px={{ base: "18px", md: "34px" }} py={{ base: "22px", md: "32px" }}>
            <DashboardSkeleton />
          </Box>
        </Box>
        <Box as="aside" display={{ base: "none", xl: "block" }} w="392px" flexShrink={0} alignSelf="stretch">
          <Box position="sticky" top="0" h="100vh" p="12px">
            <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" px="20px" py="28px" sx={GLASS}>
              <RailSkeleton />
            </Box>
          </Box>
        </Box>
      </Flex>
    );
  }

  // licznik indeksów animacji (stagger)
  let ai = 0;
  const nx = () => ai++;

  const isNewVoter = !isOrg && actions.length === 0 && model.upcomingParticipations.length === 0;

  return (
    <Flex align="flex-start">
      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1680px" mx="auto" px={{ base: "18px", md: "34px" }} py={{ base: "22px", md: "32px" }}>
          <VStack align="stretch" spacing="18px">
            {/* ── BENTO ── */}
            <Grid
              autoRows={{ base: "144px", lg: "150px" }}
              gap="14px"
              gridAutoFlow="dense"
              sx={{
                gridTemplateColumns: "repeat(2, 1fr)",
                "@media (min-width: 62em)": { gridTemplateColumns: "repeat(4, 1fr)" },
                "@media (min-width: 120em)": { gridTemplateColumns: "repeat(6, 1fr)" }
              }}
            >
              <StatusTile i={nx()} model={model} statusLine={statusLine} />

              {isOrg ? (
                <>
                  <KpiTile i={nx()} icon={PiBroadcastDuotone} value={`${ongoingVotings}`} label="Trwające" color="#1f9e18" to="/organizator/twoje-glosowania" />
                  <KpiTile i={nx()} icon={PiClockDuotone} value={`${model.plannedCount}`} label="Zaplanowane" color="#7b2ff7" to="/organizator/twoje-glosowania" />
                  <KpiTile i={nx()} icon={PiUsersDuotone} value={compactNum(model.totalVoters)} label="Uprawnieni" color="#2563eb" />
                  <KpiTile i={nx()} icon={PiChartBarDuotone} value={compactNum(model.totalVotes)} label="Oddane głosy" color="#e51f44" />
                  <KpiTile i={nx()} icon={PiTrendUpDuotone} value={`${turnout}%`} label="Frekwencja" color="#f59e0b" />
                  <KpiTile i={nx()} icon={PiLinkDuotone} value={`${model.anchoredCount}`} label="Zakotwiczone" color="#0d9488" sub="na blockchainie" />
                </>
              ) : (
                <>
                  <KpiTile i={nx()} icon={PiCheckCircleDuotone} value={`${toVote}`} label="Do oddania" color="#1f9e18" to="/organizator/udzial" />
                  <KpiTile i={nx()} icon={PiBroadcastDuotone} value={`${ongoing}`} label="Trwające" color="#7b2ff7" />
                  <KpiTile i={nx()} icon={PiChartPieSliceDuotone} value={`${resultsReady}`} label="Wyniki gotowe" color="#2563eb" />
                  <KpiTile i={nx()} icon={PiUsersThreeDuotone} value={`${model.myGroupsCount}`} label="Twoje grupy" color="#e51f44" to="/organizator/moje-grupy" />
                </>
              )}

              {/* Wymaga uwagi */}
              {actions.length > 0 && (
                <Tile i={nx()} colSpan={2} rowSpan={2} glow="#f59e0b">
                  <TileHead icon={PiLightningDuotone} title="Wymaga uwagi" color="#f59e0b" count={actions.length} />
                  <Box flex="1" minH={0} sx={SCROLL_FOG} mx="-6px" px="0">
                    <VStack align="stretch" spacing="2px">
                      {actions.slice(0, 8).map((a, k) => (
                        <AttnRow key={`${a.kind}-${k}`} item={a} />
                      ))}
                    </VStack>
                  </Box>
                </Tile>
              )}

              {/* Trwające teraz — na żywo */}
              {isOrg && liveVotings.length > 0 && (
                <Tile i={nx()} colSpan={2} rowSpan={2} glow="#1f9e18">
                  <TileHead icon={PiPulseDuotone} title="Trwające teraz" color="#1f9e18" count={liveVotings.length} to="/organizator/twoje-glosowania" />
                  <Box flex="1" minH={0} sx={SCROLL_FOG}>
                    <VStack align="stretch" spacing="3px">
                      {liveVotings.map(v => (
                        <LiveRow key={v.id} id={v.id} title={v.title} end={v.end_date ?? undefined} voters={v.voters_count ?? undefined} votes={v.votes_count ?? undefined} />
                      ))}
                    </VStack>
                  </Box>
                </Tile>
              )}

              {/* Odliczanie */}
              {isOrg && model.nextDeadline && (
                <Tile i={nx()} colSpan={2} rowSpan={1} glow="#e51f44" to={`/organizator/glosowanie/${model.nextDeadline.id}`} interactive>
                  <HStack spacing="14px" h="100%">
                    <IconChip icon={PiTimerDuotone} color="#e51f44" box="46px" size="26px" />
                    <Box flex="1" minW={0}>
                      <Text fontSize="11px" fontWeight="800" color="#e51f44" textTransform="uppercase" letterSpacing="0.05em">Kończy się za {countdown(model.nextDeadline.end)}</Text>
                      <Text fontWeight="800" color="#241536" fontSize="15px" noOfLines={2} lineHeight="1.2" mt="2px">{model.nextDeadline.title}</Text>
                    </Box>
                  </HStack>
                </Tile>
              )}

              {/* Rekord frekwencji */}
              {isOrg && model.bestTurnout && (
                <Tile i={nx()} colSpan={2} rowSpan={1} glow="#1f9e18" to={`/organizator/glosowanie/${model.bestTurnout.id}`} interactive>
                  <HStack spacing="14px" h="100%">
                    <Ring pct={model.bestTurnout.pct} color="#1f9e18" />
                    <Box flex="1" minW={0}>
                      <HStack spacing="6px">
                        <Icon as={PiTrophyDuotone} boxSize="15px" color="#1f9e18" />
                        <Text fontSize="11px" fontWeight="800" color="#1f9e18" textTransform="uppercase" letterSpacing="0.05em">Rekord frekwencji</Text>
                      </HStack>
                      <Text fontWeight="800" color="#241536" fontSize="15px" noOfLines={2} lineHeight="1.2" mt="3px">{model.bestTurnout.title}</Text>
                    </Box>
                  </HStack>
                </Tile>
              )}

              {/* Twoje głosowania */}
              {isOrg && model.recent.length > 0 && (
                <Tile i={nx()} colSpan={2} rowSpan={2}>
                  <TileHead icon={PiListChecksDuotone} title="Twoje głosowania" color="#430098" to="/organizator/twoje-glosowania" />
                  <Box flex="1" minH={0} sx={SCROLL_FOG}>
                    <VStack align="stretch" spacing="2px">
                      {model.recent.slice(0, 6).map(v => (
                        <VotingRow
                          key={v.id}
                          id={v.id}
                          title={v.title}
                          status={v.status}
                          to={v.status === VOTING_STATUSES.DRAFT ? "/organizator/twoje-glosowania" : `/organizator/glosowanie/${v.id}`}
                          voters={v.voters_count ?? undefined}
                          votes={v.votes_count ?? undefined}
                        />
                      ))}
                    </VStack>
                  </Box>
                </Tile>
              )}

              {/* Donut statusów */}
              {isOrg && statusTotal > 0 && (
                <Tile i={nx()} colSpan={2} rowSpan={2} glow="#7b2ff7">
                  <TileHead icon={PiChartPieSliceDuotone} title="Rozkład statusów" color="#7b2ff7" />
                  <DonutStatus segments={statusSegments} total={statusTotal} />
                </Tile>
              )}

              {/* Frekwencja — wykres słupkowy */}
              {isOrg && turnoutBars.length > 0 && (
                <Tile i={nx()} colSpan={{ base: 2, lg: 4 }} rowSpan={2} glow="#f59e0b">
                  <TileHead icon={PiChartBarDuotone} title="Frekwencja w głosowaniach" color="#f59e0b" />
                  <Box flex="1" minH={0} sx={SCROLL_FOG}>
                    <VStack align="stretch" spacing="2px">
                      {turnoutBars.map(b => (
                        <BarRow key={b.id} title={b.title} pct={b.pct} color="#f59e0b" />
                      ))}
                    </VStack>
                  </Box>
                </Tile>
              )}

              {/* Dowód na blockchainie */}
              {isOrg && (
                <Tile i={nx()} colSpan={2} rowSpan={2} glow="#0d9488" to={lastAnchored ? `/organizator/glosowanie/${lastAnchored.id}` : "/organizator/twoje-glosowania"} interactive>
                  <Flex direction="column" h="100%">
                    <TileHead icon={PiShieldCheckDuotone} title="Dowód na blockchainie" color="#0d9488" />
                    <Flex direction="column" align="center" justify="center" flex="1" textAlign="center" gap="4px">
                      <Box position="relative">
                        <Icon as={PiCubeDuotone} boxSize="46px" color="#0d9488" />
                      </Box>
                      <Text fontSize="40px" fontWeight="900" color="#1b1030" lineHeight="1" letterSpacing="-0.03em" mt="4px">{model.anchoredCount}</Text>
                      <Text fontSize="12.5px" color="#6b6480" fontWeight="700">
                        {plural(model.anchoredCount, "protokół zakotwiczony", "protokoły zakotwiczone", "protokołów zakotwiczonych")}
                      </Text>
                    </Flex>
                    {lastAnchored ? (
                      <Box borderTop="1px solid rgba(24,12,48,0.06)" pt="9px" mt="6px">
                        <Text fontSize="10.5px" color="#a8a2b6" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">Ostatnie</Text>
                        <HStack spacing="6px" justify="space-between">
                          <Text fontSize="12.5px" color="#2f2a3d" fontWeight="700" noOfLines={1}>{lastAnchored.title}</Text>
                          <HStack spacing="3px" color="#0d9488" flexShrink={0}>
                            <Text fontSize="11.5px" fontWeight="800">Zweryfikuj</Text>
                            <Icon as={PiArrowRightBold} boxSize="11px" />
                          </HStack>
                        </HStack>
                      </Box>
                    ) : (
                      <Text fontSize="11.5px" color="#a8a2b6" fontWeight="600" textAlign="center" pt="6px">
                        Pierwszy protokół zakotwiczy się po zakończeniu głosowania.
                      </Text>
                    )}
                  </Flex>
                </Tile>
              )}

              {/* Oś czasu terminów */}
              {isOrg && timeline.length > 0 && (
                <Tile i={nx()} colSpan={2} rowSpan={2} glow="#7b2ff7">
                  <TileHead icon={PiCalendarBlankDuotone} title="Oś czasu terminów" color="#7b2ff7" />
                  <Box flex="1" minH={0} sx={SCROLL_FOG}>
                    <VStack align="stretch" spacing={0}>
                      {timeline.map((e, k) => (
                        <TimelineRow key={`${e.kind}-${e.id}`} kind={e.kind} id={e.id} title={e.title} at={e.at} last={k === timeline.length - 1} />
                      ))}
                    </VStack>
                  </Box>
                </Tile>
              )}

              {/* Otwarte zapisy */}
              {isOrg && model.openSignups.length > 0 && (
                <Tile i={nx()} colSpan={2} rowSpan={2}>
                  <TileHead icon={PiUserPlusDuotone} title="Otwarte zapisy" color="#430098" count={model.openSignups.length} />
                  <Box flex="1" minH={0} sx={SCROLL_FOG}>
                    <VStack align="stretch" spacing="2px">
                      {model.openSignups.map(s => (
                        <HStack key={s.id} as={Link} to={`/organizator/glosowanie/${s.id}`} spacing="11px" py="8px" px="8px" borderRadius="12px" transition={`background ${EASE}`} _hover={{ bg: "rgba(67,0,152,0.05)", textDecoration: "none" }}>
                          <IconChip icon={PiUserPlusDuotone} color="#430098" box="36px" size="18px" />
                          <Box flex="1" minW={0}>
                            <Text fontWeight="700" color="#2f2a3d" fontSize="13.5px" noOfLines={1}>{s.title}</Text>
                            <Text fontSize="11.5px" color="#a8a2b6" fontWeight="600">{signupCloses(s.closesAt)}</Text>
                          </Box>
                          <Icon as={PiCaretRightBold} boxSize="13px" color="#c9c3d6" flexShrink={0} />
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </Tile>
              )}

              {/* Twój udział */}
              {model.upcomingParticipations.length > 0 && (
                <Tile i={nx()} colSpan={2} rowSpan={2}>
                  <TileHead icon={PiCheckCircleDuotone} title="Twój udział" color="#1f9e18" to="/organizator/udzial" />
                  <Box flex="1" minH={0} sx={SCROLL_FOG}>
                    <VStack align="stretch" spacing="2px">
                      {model.upcomingParticipations.map(v => (
                        <VotingRow key={v.id} id={v.id} title={v.title} status={v.status} to={`/glosowanie/${v.id}`} />
                      ))}
                    </VStack>
                  </Box>
                </Tile>
              )}
            </Grid>

            {/* Pusty stan dla nowych */}
            {isNewVoter && (
              <Flex direction="column" align="center" justify="center" py="46px" borderRadius="22px" sx={GLASS}>
                <IconChip icon={PiRocketLaunchDuotone} color="#e51f44" box="60px" size="34px" />
                <Text color="#241536" fontWeight="900" fontSize="19px" mt="14px" mb="4px" letterSpacing="-0.01em">
                  Zacznij od pierwszego głosowania
                </Text>
                <Text color="#6b6480" fontSize="14px" mb="18px" textAlign="center" maxW="380px">
                  Stwórz weryfikowalne głosowanie zakotwiczone na blockchainie w kilka minut.
                </Text>
                <Button as={Link} to="/organizator/utworz-glosowanie" leftIcon={<Icon as={PiPlusBold} />} bg="accent" color="white" borderRadius="13px" fontWeight="800" _hover={{ bg: "#c41a3a" }}>
                  Utwórz głosowanie
                </Button>
              </Flex>
            )}

            {/* Odkrywaj — pełna szerokość pod bento */}
            {model.discoverTop.length > 0 && (
              <Box>
                <PanelHeading icon={PiCompassDuotone} title="Odkrywaj głosowania" color="#7b2ff7" to="/aktywne-glosowania" />
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="12px">
                  {model.discoverTop.map(v => (
                    <MiniVotingCard key={v.id} id={v.id} title={v.title} status={v.status} />
                  ))}
                </SimpleGrid>
              </Box>
            )}

            {/* Prawy panel zwinięty do głównej kolumny na węższych ekranach (<xl) */}
            <Box display={{ base: "block", xl: "none" }} borderRadius="22px" overflow="hidden" position="relative" px="20px" py="28px" sx={GLASS}>
              <RightRail model={model} />
            </Box>
          </VStack>
        </Box>
      </Box>

      {/* ── PRAWY SIDEBAR — glass-card z poświatą + scroll-fog ── */}
      <Box as="aside" display={{ base: "none", xl: "block" }} w="392px" flexShrink={0} alignSelf="stretch">
        <Box position="sticky" top="0" h="100vh" p="12px">
          <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
            <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 26%, rgba(124,58,237,0.12), transparent 72%), radial-gradient(52% 40% at 50% 110%, rgba(37,99,235,0.08), transparent 72%)" }} />
            <VStack
              position="relative"
              align="stretch"
              spacing="0"
              h="100%"
              overflowY="auto"
              px="20px"
              py="28px"
              sx={{
                maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 26px), transparent 100%)",
                WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 26px), transparent 100%)"
              }}
            >
              <RightRail model={model} />
            </VStack>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
});

const OrganizerDashboardViewWithModel = withModel<OrganizerDashboardModel>(OrganizerDashboardView, OrganizerDashboardModel);

export default OrganizerDashboardViewWithModel;
